import React, { useState } from 'react';
import {
  GitBranch, Plus, MoreHorizontal, ArrowRight, Users, Clock,
  Shield, AlertTriangle, CheckCircle, ChevronRight, GripVertical,
  Edit2, Trash2, Copy
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { cn } from '../../lib/utils';

const mockRules = [
  {
    id: 'rule-1',
    name: 'Access Requests',
    description: 'Route access and permission requests',
    conditions: [
      { field: 'category', operator: 'equals', value: 'access' },
      { field: 'keywords', operator: 'contains', value: 'access, permission, grant' }
    ],
    assignTo: { type: 'team', value: 'Identity & Access' },
    approval: { required: true, approvers: ['manager'], timeout: '4h' },
    escalation: { enabled: true, timeout: '8h', to: 'org_admin' },
    priority: 1,
    enabled: true,
    matchCount7d: 156
  },
  {
    id: 'rule-2',
    name: 'Password Resets',
    description: 'Self-service password reset requests',
    conditions: [
      { field: 'keywords', operator: 'contains', value: 'password, reset, forgot, locked' }
    ],
    assignTo: { type: 'workflow', value: 'Password Reset Workflow' },
    approval: { required: false },
    escalation: { enabled: true, timeout: '2h', to: 'IT Operations' },
    priority: 2,
    enabled: true,
    matchCount7d: 234
  },
  {
    id: 'rule-3',
    name: 'Hardware Requests',
    description: 'New device and equipment requests',
    conditions: [
      { field: 'category', operator: 'equals', value: 'hardware' }
    ],
    assignTo: { type: 'team', value: 'Endpoint Management' },
    approval: { required: true, approvers: ['manager', 'finance'], timeout: '24h' },
    escalation: { enabled: true, timeout: '48h', to: 'IT Operations' },
    priority: 3,
    enabled: true,
    matchCount7d: 45
  },
  {
    id: 'rule-4',
    name: 'Security Incidents',
    description: 'Critical security alerts and incidents',
    conditions: [
      { field: 'category', operator: 'equals', value: 'security' },
      { field: 'severity', operator: 'gte', value: 'high' }
    ],
    assignTo: { type: 'team', value: 'Security' },
    approval: { required: false },
    escalation: { enabled: true, timeout: '30m', to: 'security_lead' },
    priority: 0,
    enabled: true,
    matchCount7d: 12
  },
  {
    id: 'rule-5',
    name: 'Default Catch-All',
    description: 'Unmatched requests go to IT Operations',
    conditions: [
      { field: 'always', operator: 'matches', value: 'all' }
    ],
    assignTo: { type: 'team', value: 'IT Operations' },
    approval: { required: false },
    escalation: { enabled: true, timeout: '4h', to: 'manager' },
    priority: 100,
    enabled: true,
    matchCount7d: 89
  }
];

const teams = ['IT Operations', 'Security', 'Identity & Access', 'Endpoint Management'];

export const Routing = () => {
  const [rules, setRules] = useState(mockRules);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const toggleRule = (ruleId) => {
    setRules(rules.map(r =>
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    ));
  };

  const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Routing Rules</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Define how incoming requests are routed to teams and workflows
              </p>
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Rule
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Routing Rule</DialogTitle>
                  <DialogDescription>
                    Define conditions to match requests and specify routing behavior
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Rule Name</Label>
                    <Input placeholder="e.g., VPN Access Requests" />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input placeholder="What does this rule match?" />
                  </div>
                  <div className="space-y-2">
                    <Label>Route To</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select destination" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="team-it">Team: IT Operations</SelectItem>
                        <SelectItem value="team-security">Team: Security</SelectItem>
                        <SelectItem value="team-identity">Team: Identity & Access</SelectItem>
                        <SelectItem value="workflow-password">Workflow: Password Reset</SelectItem>
                        <SelectItem value="workflow-access">Workflow: Access Request</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Require Approval</Label>
                      <p className="text-xs text-muted-foreground">Actions need manager sign-off</p>
                    </div>
                    <Switch />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={() => setShowCreateDialog(false)}>Create Rule</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <GitBranch className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">How Routing Works</p>
                <p className="text-sm text-blue-700 mt-1">
                  Rules are evaluated in priority order (lowest number first).
                  The first matching rule determines routing.
                  If no rules match, the default catch-all rule applies.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          {sortedRules.map((rule, idx) => (
            <Card
              key={rule.id}
              className={cn(
                "transition-colors",
                !rule.enabled && "opacity-60",
                rule.priority === 0 && "border-red-200"
              )}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="flex items-center gap-2 text-muted-foreground cursor-grab">
                    <GripVertical className="w-4 h-4" />
                    <span className="text-xs font-mono w-6">{rule.priority}</span>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-foreground">{rule.name}</h3>
                      {rule.priority === 0 && (
                        <Badge variant="destructive" className="text-xs">High Priority</Badge>
                      )}
                      {rule.name === 'Default Catch-All' && (
                        <Badge variant="secondary" className="text-xs">Fallback</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{rule.description}</p>

                    <div className="mt-3 flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Conditions:</span>
                        <div className="flex flex-wrap gap-1">
                          {rule.conditions.map((cond, cidx) => (
                            <Badge key={cidx} variant="outline" className="text-xs font-mono">
                              {cond.field} {cond.operator} {cond.value}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <Badge className={cn(
                          rule.assignTo.type === 'team' ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
                        )}>
                          {rule.assignTo.type === 'team' ? (
                            <><Users className="w-3 h-3 mr-1" />{rule.assignTo.value}</>
                          ) : (
                            <><GitBranch className="w-3 h-3 mr-1" />{rule.assignTo.value}</>
                          )}
                        </Badge>
                      </div>

                      {rule.approval.required && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Shield className="w-3 h-3" />
                          <span>Approval: {rule.approval.approvers.join(', ')}</span>
                          <span className="text-muted-foreground/60">({rule.approval.timeout})</span>
                        </div>
                      )}

                      {rule.escalation.enabled && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <AlertTriangle className="w-3 h-3" />
                          <span>Escalate after {rule.escalation.timeout}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-lg font-semibold text-foreground">{rule.matchCount7d}</p>
                      <p className="text-xs text-muted-foreground">matches (7d)</p>
                    </div>

                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={() => toggleRule(rule.id)}
                    />

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit Rule
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <ChevronRight className="w-4 h-4 mr-2" />
                          View Matches
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {rule.name !== 'Default Catch-All' && (
                          <DropdownMenuItem className="text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-base">Approval Chain Reference</CardTitle>
            <CardDescription>Who can approve actions at each level</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-2">Manager Approval</h4>
                <p className="text-xs text-muted-foreground">
                  Team managers can approve standard requests within their team scope.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-2">Finance Approval</h4>
                <p className="text-xs text-muted-foreground">
                  Required for requests with cost implications (hardware, licenses).
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="text-sm font-medium text-foreground mb-2">Org Admin Approval</h4>
                <p className="text-xs text-muted-foreground">
                  Escalation path for high-risk or cross-team requests.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Routing;
